import React from 'react';
import { Dropdown, Tooltip, type MenuProps } from 'antd';
import { ExclamationCircleOutlined, MoreOutlined } from '@ant-design/icons';
import styled from 'styled-components';

interface IProps {
  title: string;
  /**
   * A second line under the title. Repeatable blocks share a title, so this is
   * the only thing that tells one from another — on one line it was the part
   * that got cut.
   */
  subtitle?: string;
  /** The title still has template notation to resolve. */
  unresolved?: boolean;
  /** An imported heading still to keep or delete. */
  unmatched?: boolean;
  /** One below is, and the row may be collapsed. */
  unmatchedBelow?: boolean;
  onClick: () => void;
  dropdownItems: MenuProps['items'];
}

const MenuItemTitle: React.FC<IProps> = ({
  title,
  subtitle,
  unresolved,
  unmatched,
  unmatchedBelow,
  onClick,
  dropdownItems,
}) => {
  const showDropdown = Array.isArray(dropdownItems) && dropdownItems.length > 0;
  return (
    <MenuTitleWrapper onClick={onClick}>
      <TextColumn>
        <TitleLine>
          {unresolved && (
            <Tooltip title="The title still has choices to make or blanks to fill in">
              <Unresolved />
            </Tooltip>
          )}
          {(unmatched || unmatchedBelow) && (
            <Tooltip
              title={
                unmatched
                  ? 'Imported heading that is not a predefined section — keep it as a custom section or delete it'
                  : 'Has imported headings to keep or delete'
              }
            >
              <Unresolved />
            </Tooltip>
          )}
          <MenuTitle title={title}>{title}</MenuTitle>
        </TitleLine>
        {subtitle && <Subtitle title={subtitle}>{subtitle}</Subtitle>}
      </TextColumn>

      {showDropdown && (
        <div
          style={{ display: 'flex', alignItems: 'center' }}
          onClick={e => e.stopPropagation()}
        >
          <Dropdown menu={{ items: dropdownItems }} trigger={['click']}>
            <MoreOutlined />
          </Dropdown>
        </div>
      )}
    </MenuTitleWrapper>
  );
};

const MenuTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 8px;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
  cursor: pointer;
  padding-left: 8px;
  max-width: calc(100% - 50px);
`;

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

/* On the svg: EpiMenu forces every icon in the sidebar white. */
const Unresolved = styled(ExclamationCircleOutlined)`
  flex-shrink: 0;

  svg {
    color: ${({ theme }) => theme?.colors?.warning} !important;
  }
`;

const MenuTitle = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  line-height: 1.4;
  text-transform: uppercase;
  /* Stated, not inherited: a selected row turns its label primary, and the
     subtitle is a qualifier rather than part of the name. */
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
`;

export default MenuItemTitle;
